import React from 'react';
import { DateInput } from '@innovationnorway/component-library';
import { Controller } from 'react-hook-form';
import { DeliveryType } from 'src/api/v2';
import useDeliveryTypeOptions from '../hooks/use-delivery-type-options';
import { formatDateInputDate } from 'src/utils/FormatValue';
import useDeliveryForm, { DeliveryData } from '../hooks/subforms/use-delivery-form';
import { SubFormProps } from '../hooks/use-service-sub-form';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Field,
  Fieldset,
  Heading,
  Label,
  Radio,
  RichTextEditor,
  ValidationMessage,
} from '@innovationnorway/cl-react';

const DeliveryPage = (props: SubFormProps<DeliveryData>) => {
  const { form, rules } = useDeliveryForm(props);

  const [hasDeadline, setHasDeadline] = React.useState<boolean | undefined>(() => {
    if (props.data.applicationDeadline) return true;
    if (props.data.applicationDeadline === null) return false;
    return undefined;
  });
  const [hasUserFee, setHasUserFee] = React.useState<boolean | undefined>(
    props.data.hasUserFee !== undefined ? props.data.hasUserFee : undefined,
  );
  const { control, setValue, getValues } = form;
  const { t: tServices } = useTranslation('services');

  const { deliveryTypeOptions } = useDeliveryTypeOptions(getValues('deliveryTypes'));
  const { errors } = props;

  return (
    <div>
      <Heading>{tServices('delivery.title')}</Heading>
      <div className="display--flex flex-direction--column gap--5 margin-top--2">
        <div>
          <Controller
            control={control}
            name="deliveryTypes"
            rules={rules.deliveryTypes}
            render={({ field, fieldState }) => (
              <Fieldset>
                <Fieldset.Legend data-size="md">{tServices('delivery.type.label')}</Fieldset.Legend>
                <Fieldset.Description>{tServices('delivery.type.help')}</Fieldset.Description>
                {deliveryTypeOptions.map((option) => (
                  <Checkbox
                    key={String(option.value)}
                    label={option.text}
                    value={String(option.value)}
                    checked={(field.value || []).includes(option.value as DeliveryType)}
                    onChange={(e) => {
                      const { checked, value } = e.currentTarget;
                      const currentValues = field.value || [];
                      if (checked) {
                        if (!currentValues.includes(value as DeliveryType)) {
                          setValue('deliveryTypes', [...currentValues, value as DeliveryType]);
                        }
                      } else {
                        setValue(
                          'deliveryTypes',
                          currentValues.filter((item) => item !== value),
                        );
                      }
                      form.trigger('deliveryTypes');
                    }}
                  />
                ))}
                {(fieldState.error || props.errors.deliveryTypes) && (
                  <ValidationMessage>
                    {fieldState.error?.message || props.errors.deliveryTypes?.message}
                  </ValidationMessage>
                )}
              </Fieldset>
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="deliveryDescription"
            rules={rules.deliveryDescription}
            render={({ fieldState, field }) => {
              return (
                <Field>
                  <Label>{tServices('delivery.description.label')}</Label>
                  <Field.Description data-size="sm">
                    {tServices('delivery.description.help')}
                  </Field.Description>
                  <RichTextEditor
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    initialValue={field.value}
                    counter={500}
                    rows={6}
                  />
                  {(fieldState.error?.message || errors.deliveryDescription?.message) && (
                    <ValidationMessage>
                      {fieldState.error?.message || errors.deliveryDescription?.message}
                    </ValidationMessage>
                  )}
                </Field>
              );
            }}
          />
        </div>
        <div>
          <Fieldset>
            <Fieldset.Legend data-size="md">{tServices('delivery.deadline.question')}</Fieldset.Legend>
            <Fieldset.Description>{tServices('delivery.deadline.help')}</Fieldset.Description>
            <Radio
              label={tServices('form.yes')}
              name="hasDeadline"
              value="true"
              checked={hasDeadline === true}
              onChange={() => {
                setHasDeadline(true);
                setValue('applicationDeadline', undefined);
              }}
            />
            <Radio
              label={tServices('form.no')}
              name="hasDeadline"
              value="false"
              checked={hasDeadline === false}
              onChange={() => {
                setHasDeadline(false);
                setValue('applicationDeadline', undefined);
              }}
            />
            {(Object.keys(form.formState.errors).length > 0 || Object.keys(errors).length > 0) &&
              hasDeadline === undefined && (
                <ValidationMessage>{tServices('delivery.deadline.question.required')}</ValidationMessage>
              )}
          </Fieldset>
          {hasDeadline && (
            <Controller
              control={control}
              name="applicationDeadline"
              rules={rules.applicationDeadline}
              render={({ field, fieldState }) => (
                <Field className="margin-top--2">
                  <Label>{tServices('delivery.deadline.label')}</Label>
                  <Field.Description data-size="sm">
                    {tServices('delivery.deadline.helpText')}
                  </Field.Description>
                  <DateInput
                    className="width--60"
                    {...field}
                    value={field.value ? formatDateInputDate(field.value) : undefined}
                    errorMsg={fieldState.error?.message}
                  />
                </Field>
              )}
            />
          )}
        </div>
        <div>
          <Controller
            control={control}
            name="hasUserFee"
            rules={rules.hasUserFee}
            render={({ field, fieldState }) => (
              <Fieldset>
                <Fieldset.Legend data-size="md">{tServices('delivery.userFee.label')}</Fieldset.Legend>
                <Fieldset.Description>{tServices('delivery.userFee.help')}</Fieldset.Description>
                <Radio
                  label={tServices('form.yes')}
                  name="hasUserFee"
                  value="true"
                  checked={field.value === true}
                  onChange={() => {
                    setHasUserFee(true);
                    field.onChange(true);
                  }}
                />
                <Radio
                  label={tServices('form.no')}
                  name="hasUserFee"
                  value="false"
                  checked={field.value === false}
                  onChange={() => {
                    setHasUserFee(false);
                    field.onChange(false);
                    setValue('userFeeComments', undefined);
                  }}
                />
                {fieldState.error && <ValidationMessage>{fieldState.error.message}</ValidationMessage>}
              </Fieldset>
            )}
          />
          {hasUserFee && (
            <Controller
              control={control}
              name="userFeeComments"
              rules={rules.userFeeComments}
              render={({ field, fieldState }) => {
                return (
                  <Field>
                    <Label>{tServices('delivery.userFee.comment.label')}</Label>
                    <RichTextEditor
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      initialValue={field.value}
                      data-size="lg"
                      counter={500}
                      rows={6}
                    />
                    {(fieldState.error?.message || errors.userFeeComments?.message) && (
                      <ValidationMessage>
                        {fieldState.error?.message || errors.userFeeComments?.message}
                      </ValidationMessage>
                    )}
                  </Field>
                );
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryPage;
